// Danh sách sản phẩm của đơn cơ bản (store.ts): mỗi lần chọn là 1 dòng riêng (lineKey), không gộp theo mã
// để KD nhập giá khác nhau cho cùng 1 mã. Khi gửi (modal xác nhận + payload) gộp lại các dòng cùng mã + cùng giá.
// File thuần (không import global/Global.ts) để test được bằng tsc + node.

type Line = {
  name?: string;
  Quantity?: number;
  giaBanSi?: number;
  giaBanLe?: number;
  id?: number;
  details?: any[];
  lineKey?: string;
  orderItemId?: number; // id item của đơn (dòng nạp từ đơn khi cập nhật). Dòng chọn mới thì không có
};

let lineSeq = 0;

const newLine = <T extends Line>(device: T, patch: Partial<T> = {}): T & { lineKey: string } => ({
  ...device,
  ...patch,
  lineKey: `L${++lineSeq}`
});

const has = (v: any) => v !== null && v !== undefined;

type PayloadItem = {
  id?: number;
  maSanPham?: string;
  quantity?: number;
  total?: number;
  cost?: number;
  vatValue?: number;
};

// Payload `items` khi gửi: gộp dòng cùng mã + cùng giá + cùng VAT (cộng SL, tổng); khác giá giữ riêng.
// Cập nhật đơn (có id): item cũ giữ id của nó, item mới id = 0.
//   VD: BO01 SL1 giá 1200 (id 0, mới) + BO01 SL2 giá 1200 (id 55, cũ) -> 1 dòng id 55, SL 3
//   VD: BO01 id 55 + BO01 id 56 (2 dòng cũ) -> giữ 2 dòng riêng, không mất id nào
export const mergeSameItems = <T extends PayloadItem>(items: T[]): T[] => {
  const byKey = new Map<string, T>();

  items.forEach(i => {
    let k = `${i.maSanPham}|${i.cost}|${i.vatValue || 0}`;

    // Đã có dòng cũ khác id cùng mã + giá -> tách khoá riêng theo id
    const daCo = byKey.get(k);
    if (daCo && daCo.id && i.id && daCo.id !== i.id) k = `${k}|${i.id}`;

    const cur = byKey.get(k);
    if (!cur) {
      byKey.set(k, { ...i });
      return;
    }

    const merged = {
      ...cur,
      quantity: (cur.quantity || 0) + (i.quantity || 0),
      total: (cur.total || 0) + (i.total || 0)
    };
    if (!cur.id && i.id) merged.id = i.id; // dòng trước là dòng mới -> lấy id dòng cũ
    byKey.set(k, merged);
  });

  return Array.from(byKey.values());
};

/**
 * Đơn ĐL lắp giùm (9): danh sách SP dựng lại từ chi tiết các xe (mỗi chi tiết 1 chiếc), kết quả đưa vào addOrderLines.
 * tachDong = true: mỗi chi tiết 1 dòng SL 1, giá lấy lần lượt theo các dòng cũ cùng mã (dòng SL 2 tính là 2 lần).
 *   VD: dòng cũ CAM01 1200, CAM01 900; xe 1 CAM01, xe 2 CAM01 -> 2 dòng CAM01 giá 1200 và 900
 * tachDong = false (bản cũ): quantities = null để addOrderLines cộng dồn theo mã, giá lấy dòng đầu cùng mã.
 * Giá 0 / không có dòng cũ -> null (addOrderLines lấy giá danh mục), giữ như hàm cũ handleCalSumByDetail.
 */
export const linesFromDetails = (sets: any[], preLines: any[], tachDong = false) => {
  const devices: string[] = [];
  const price: any[] = [];

  // --- BƯỚC 1: hàng đợi giá theo mã. VD CAM01 SL 2 giá 1000 -> { CAM01: [1000, 1000] } ---
  const giaTheoMa: Record<string, any[]> = {};
  (preLines || []).forEach((p: any) => {
    if (!giaTheoMa[p.maSanPham]) giaTheoMa[p.maSanPham] = [];
    const soLan = Math.max(1, Number(p.quantity || 0));
    for (let k = 0; k < soLan; k++) giaTheoMa[p.maSanPham].push(p.cost);
  });

  // --- BƯỚC 2: mỗi chi tiết của từng xe là 1 phần tử ---
  (sets || []).forEach((set: any) => {
    (set.details || []).forEach((d: any) => {
      const hangDoi = giaTheoMa[d.maSanPham] || [];
      let gia = null;
      if (tachDong) gia = hangDoi.shift();
      else gia = hangDoi[0];
      devices.push(d.maSanPham);
      price.push(gia || null);
    });
  });

  return { devices, quantities: tachDong ? devices.map(() => 1) : null, price };
};

export const isSameLine = (a: { lineKey?: string }, b: { lineKey?: string }) => !!a.lineKey && a.lineKey === b.lineKey;

/**
 * Chọn sản phẩm: mỗi mã thêm 1 dòng mới với SL `quantity` (mặc định 1).
 * tachDong = false (bản cũ, cờ OMConfig TachDongSanPham tắt): mã đã có dòng thì cộng SL vào dòng đó.
 *   VD: đã có BO01 SL 2, chọn thêm BO01 -> BO01 SL 3 (không thêm dòng)
 */
export const addNewLines = <T extends Line>(
  list: T[],
  catalog: T[],
  names: string[],
  quantity?: number,
  tachDong = true
) => {
  let result: T[] = [...(list || [])];
  names.forEach(n => {
    const device = catalog.find(p => p.name === n);
    if (!device) return;
    const existing = !tachDong && result.find(l => l.name === n);
    if (existing) {
      result = result.map(l => (l === existing ? { ...l, Quantity: (l.Quantity || 0) + (quantity || 1) } : l));
      return;
    }
    result.push(newLine(device, { Quantity: quantity || 1 } as Partial<T>));
  });
  return result;
};

/**
 * Nạp item của đơn vào danh sách: mỗi item 1 dòng (giữ giá + id riêng).
 * quantity = null là tính lại từ chi tiết (đơn 9/12, mỗi phần tử 1 chiếc) -> cộng dồn theo mã.
 * tachDong = false (bản cũ): luôn cộng dồn theo mã, giá/id lấy theo item sau cùng.
 */
export const addOrderLines = <T extends Line>(
  list: T[],
  catalog: T[],
  names: string[],
  quantity: any[] | null,
  prices: any[] | null,
  details?: any[] | null,
  ids?: any[] | null,
  tachDong = true
) => {
  let result: T[] = [...(list || [])];
  names.forEach((n, i) => {
    const device = catalog.find(p => p.name === n);
    if (!device) return;
    const price = prices?.[i];
    const existing = (!quantity || !tachDong) && result.find(l => l.name === n);
    if (existing) {
      result = result.map(l =>
        l === existing
          ? {
              ...l,
              Quantity: (l.Quantity || 0) + (quantity ? Number(quantity[i] || 0) : 1),
              giaBanSi: has(price) ? price : l.giaBanLe,
              id: has(ids?.[i]) ? ids![i] : l.id,
              details: details ? (l.details || []).concat(details[i]) : l.details
            }
          : l
      );
      return;
    }
    const patch: Partial<Line> = { Quantity: quantity ? quantity[i] : 1 };
    if (has(price) && price >= 0) patch.giaBanSi = price;
    if (has(ids?.[i]) && ids![i] >= 0) patch.id = ids![i];
    if (has(ids?.[i]) && ids![i] > 0) patch.orderItemId = ids![i]; // gửi lại khi cập nhật đơn
    if (details) patch.details = details[i];
    result.push(newLine(device, patch as Partial<T>));
  });
  return result;
};
